import React, { useContext, useCallback } from "react";
import { AppFwAppProviderPropsContext } from "../AppFwApp";
import DefaultAppFwLayout from "./default/DefaultAppFwLayout.tsx";
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { ArrowBack, Close, MoreVert } from "@mui/icons-material";
import { AppFwContextMenuContext, AppFwContextMenuAction } from "../context-menu/app-fw-context-menu.tsx";

export interface AppFwOverlayProps {
    readonly children: React.ReactNode;
    readonly title: string;
    readonly forceClose?: boolean;
    readonly avatar?: boolean;
}

export default function AppFwOverlay(props: AppFwOverlayProps) {

    const { accountEl } = useContext(AppFwAppProviderPropsContext)
    const location = useLocation()
    const [actions, setActions] = React.useState<AppFwContextMenuAction[]>([])

    const appTitle = useCallback(() => {

        let backUrl = location.state?.backgroundLocation?.pathname
            || location.state?.from?.pathname
            || "/"
        let replace = backUrl === "/"
        let prevFrom = location.state?.from?.state?.from || location.state?.backgroundLocation?.state?.from
        let closeSemantic =
            props.forceClose === true ||
            location.state?.backgroundLocation !== undefined ||
            backUrl === "/"

        // Preserve all state except navigation-specific properties
        const { from, backgroundLocation, ...otherState } = location.state || {}

        return (
            <Stack direction="row" alignItems="center">
                <Link
                    to={backUrl}
                    replace={replace}
                    state={{
                        ...otherState,
                        from: prevFrom,
                    }}
                >
                    <IconButton>
                        {closeSemantic ? <Close /> : <ArrowBack />}
                    </IconButton>
                </Link>
                <Typography variant="h6" sx={{ userSelect: 'none' }}>
                    {props.title}
                </Typography>
            </Stack>
        )
    }, [location])

    const contextMenuValue = React.useMemo(() => ({
        setActions,
        actions
    }), [actions])

    const ToolbarActions = useCallback(() => (
        <OverlayToolbarActions avatar={props.avatar} AccountEl={accountEl} />
    ), [props.avatar, accountEl])

    return (
        <AppFwContextMenuContext.Provider value={contextMenuValue}>
            <DefaultAppFwLayout
                hideNavigation
                slots={{
                    toolbarActions: ToolbarActions,
                    appTitle: appTitle,
                }}
            >
                {props.children}
            </DefaultAppFwLayout>
        </AppFwContextMenuContext.Provider>
    )
}

function OverlayToolbarActions({ avatar, AccountEl }: { avatar?: boolean, AccountEl?: any }) {
    const { actions } = useContext(AppFwContextMenuContext);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <Stack direction="row" gap={1} alignItems="center">
            {actions.length > 0 && (
                <>
                    <IconButton
                        aria-label="more"
                        id="long-button"
                        aria-controls={open ? 'long-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="true"
                        onClick={handleClick}
                    >
                        <MoreVert />
                    </IconButton>
                    <Menu
                        id="long-menu"
                        MenuListProps={{
                            'aria-labelledby': 'long-button',
                        }}
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        {actions.map((action, index) => (
                            <MenuItem key={action.key || index} onClick={() => {
                                action.onClick();
                                handleClose();
                            }}>
                                {action.icon && (
                                    <ListItemIcon>
                                        {action.icon}
                                    </ListItemIcon>
                                )}
                                <ListItemText>{action.text}</ListItemText>
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
            {avatar !== false && AccountEl && <AccountEl />}
        </Stack>
    )
}